import { useGuaranteesServiceGuaranteesInsuredRisksList } from '@insurance/api/queries'
import { GuaranteeTypeEnum } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { Form } from 'antd'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import QuillEditor from 'react-quill-new'

import { fillInsuredRisksVariables, formProps } from './utils'

import 'react-quill-new/dist/quill.snow.css'

/** An "empty" Quill value is still `<p><br></p>`, which `required` accepts. */
const stripHtml = (value?: string) => value?.replace(/<[^>]+>/g, '').trim()

export const GuaranteedObligationField = () => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const form = Form.useFormInstance()

  const { guaranteedObligationProps, guaranteeTypeProps } = formProps(t)
  const name = guaranteedObligationProps.name

  // The blank of the type whose text is currently in the field.
  const appliedTypeRef = useRef<GuaranteeTypeEnum>()
  const templateRef = useRef<string>()
  // Once the manager rewrites the blank himself, it stops following the form.
  const isEditedRef = useRef(false)
  // Quill normalizes the HTML it is given, so what comes back from the store is
  // not always what was written — the last written text is what tells whether
  // there is anything new to write.
  const lastFilledRef = useRef<string>()

  // The blanks are maintained in Products → Guarantees insurance.
  const { data: insuredRisks } = useGuaranteesServiceGuaranteesInsuredRisksList(
    {}
  )

  const guaranteeType: GuaranteeTypeEnum | undefined = Form.useWatch(
    guaranteeTypeProps.name
  )

  // Any field can be named by a `{variable}`, so the whole form is watched.
  const values = Form.useWatch([], form)

  const fill = useCallback(
    (blank: string) => {
      const text = fillInsuredRisksVariables(blank, (fieldName) =>
        form.getFieldValue(fieldName)
      )

      if (text === lastFilledRef.current) return

      lastFilledRef.current = text

      form.setFieldValue(name, text)
    },
    [form, name]
  )

  useEffect(() => {
    // A new order opens with a type already selected, and the list is usually
    // still in flight then — so the blank is applied from the value, not from
    // the select's `onChange`.
    if (!guaranteeType || !insuredRisks) return

    if (appliedTypeRef.current === guaranteeType) return

    // Nothing written yet means the blank of the first type it sees; anything
    // else is an order opened for edit, whose text stays as it was sold.
    const isFirstFill = !appliedTypeRef.current

    appliedTypeRef.current = guaranteeType

    if (isFirstFill && stripHtml(form.getFieldValue(name))) return

    const { text = '' } =
      insuredRisks.find((item) => item.guarantee_type === guaranteeType) || {}

    isEditedRef.current = false
    lastFilledRef.current = undefined
    templateRef.current = text

    fill(text)
  }, [guaranteeType, insuredRisks, form, name, fill])

  // `{variables}` name form fields, so the blank follows what is typed under it
  // for as long as the manager has not rewritten it himself.
  useEffect(() => {
    if (templateRef.current === undefined || isEditedRef.current) return

    fill(templateRef.current)
  }, [values, fill])

  return (
    <Form.Item
      {...guaranteedObligationProps}
      rules={[{ ...required, transform: stripHtml }]}
      getValueFromEvent={(value: string, _delta: unknown, source: string) => {
        if (source === 'user') isEditedRef.current = true

        return value
      }}
    >
      <QuillEditor placeholder={guaranteedObligationProps.label} />
    </Form.Item>
  )
}
